/**
 * Challan Column Definitions and Validation Schema for New TDS Return Forms 140 and 144
 * Applicable from FY 2026-27 onwards.
 *
 * Key changes from legacy 26Q/27Q challan:
 * - Removed: individual TDS, Surcharge, Education Cess (retained in Allocate)
 * - Added: "Total Tax deducted" (computed: TDS + Surcharge + Ed. Cess from Allocate)
 * - Renamed: Interest → Total interest, Fee → Total fee, Penalty/Others → Total penalty/others
 * - Logic: "Total Amount" = Total Tax deducted + Total Interest + Total fees + Total penalty/others
 * - Removed: "Interest to be allocated/apportioned", "Others"
 * - Renamed: BSR Code and Challan Serial No. labels (removed "Form No. 24G" reference)
 */

import { z } from 'zod'
import {
  BOOK_ENTRY_DEPOSIT_OPTIONS,
  MINOR_HEAD_CHALLAN_DATA,
} from './allocateColumn'
import { numeric12_2, mandatoryNumeric12_2 } from './validationHelpers'

/**
 * Zod validation schema for Challan rows in new Forms 140/144.
 *
 * Differences from legacy returnsChallanRowSchema:
 * - Removed: tds, surcharge, educationCess, others (individual fields)
 * - Added: totalTaxDeducted (mandatory, computed from allocate)
 * - Retained: interest, fee, penalty, bsrCode, challanSerialNo, dateOfTransferVoucher,
 *             totalAmountAsPerChallan, sectionCode
 */
export const returnsChallanRowSchemaNew = z.object({
  challanSerialNo: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^\d{1,9}$/, { message: 'Must be numeric and up to 9 digits.' }),
  bsrCode: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^\d{7}$/, { message: 'Should contain exactly 7 digits.' }),
  dateOfTransferVoucher: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, {
      message: 'Date format should be DD/MM/YYYY',
    }),
  totalAmountAsPerChallan: mandatoryNumeric12_2,
  totalTaxDeducted: mandatoryNumeric12_2,
  interest: numeric12_2,
  fee: numeric12_2,
  penalty: numeric12_2,
  sectionCode: z
    .string()
    .nonempty({ message: 'Section as per Challan is mandatory' }),
  others: numeric12_2,
  interestAllocated: numeric12_2,
})

export const RETURNS_CHALLAN_COLUMNS_140 = [
  {
    id: 'serialNo',
    label: 'Serial no.',
    width: '150px',
  },
  {
    id: 'updatedModeOfChallan',
    label: 'Update mode of challan',
    width: '250px',
  },
  {
    id: 'sectionCode',
    label: 'Section code',
    width: '160px',
    editable: true,
    isFormatted: false,
  },
  {
    id: 'totalTaxDeducted',
    label: 'Total Tax deducted',
    width: '180px',
    align: 'center',
    tooltip: 'Sum of TDS + Surcharge + Education Cess from Allocate page',
  },
  {
    id: 'interest',
    label: 'Total interest',
    width: '150px',
    editable: true,
  },
  {
    id: 'fee',
    label: 'Total fee',
    width: '150px',
    editable: true,
  },
  {
    id: 'penalty',
    label: 'Total penalty/others',
    width: '180px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '160px',
    align: 'center',
  },
  {
    id: 'totalAmountAsPerChallan',
    label: 'Total amount as per challan/ book adjustment',
    width: '240px',
    align: 'center',
    tooltip:
      'Sum of Total Tax deducted + Total interest + Total fee + Total penalty/others',
  },
  {
    id: 'chequeNo',
    label: 'Cheque/ DD no. (if any)',
    width: '160px',
  },
  {
    id: 'lastBsrCode',
    label: 'Last BSR code/ receipt no.',
    width: '160px',
    isFormatted: false,
  },
  {
    id: 'bsrCode',
    label: 'BSR code/ receipt number',
    width: '250px',
    align: 'center',
    editable: true,
    isFormatted: false,
  },
  {
    id: 'lastDateOfTaxDeposited',
    label: 'Last date on which tax deposited',
    width: '250px',
    align: 'center',
  },
  {
    id: 'dateOfTransferVoucher',
    label:
      'Date on which amount deposited through challan/ date of transfer voucher (DD/MM/YYYY)',
    width: '350px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastChallanSerialNo',
    label: 'Last DDO/ transfer voucher/ challan serial no.',
    width: '300px',
    align: 'center',
    isFormatted: false,
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no./ DDO serial no.',
    width: '300px',
    align: 'center',
    editable: true,
    isFormatted: false,
  },
  {
    id: 'modeOfDepositThroughBookAdjustment',
    label:
      'Mode of payment of tax (by way of challan/by way of book adjustment)',
    width: '350px',
    editable: true,
    type: 'dropdown',
    options: BOOK_ENTRY_DEPOSIT_OPTIONS,
    align: 'center',
  },
  {
    id: 'minorHeadOfChallan',
    label: 'Minor head of challan',
    width: '200px',
    editable: true,
    type: 'dropdown',
    options: MINOR_HEAD_CHALLAN_DATA,
    align: 'center',
    isFormatted: false,
  },
   {
    id: 'interestAllocated',
    label: 'Interest to be allocated/apportioned',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'others',
    label: 'Others',
    width: '150px',
    editable: true,
  },
  {
    id: 'challanBalanceAsPerConsolidatedFile',
    label: 'Challan balance as per consolidated file',
    width: '250px',
    align: 'center',
  },
]

// Form 144 challan structure is identical to Form 140
export const RETURNS_CHALLAN_COLUMNS_144 = RETURNS_CHALLAN_COLUMNS_140
